import React, {useEffect, useRef, useState} from 'react'
import {
    Box,
    Flex,
    IconButton,
    useBreakpointValue
} from '@chakra-ui/react'
import PropTypes from 'prop-types'
import {Swiper, SwiperSlide} from 'swiper/react'
import {Autoplay} from 'swiper/modules'
import {getGDIHideStyles} from '../../utils/gdi-styles'
import {useIsAudienceTargetMatch} from '../../utils/gdi-audience-targeting'
import {getAllExpandedFromContentArea} from '../../utils/gdi-content-area'
import {renderComponentsSwitch} from '../../../utils/contentRenderer'
import GDITooltip from '../../common/gdiTooltip'
import {Pause, Play} from '../../../../icons'
import {useHeaderContext} from '../../../../../contexts/header-context'
import styles from './styles'

/* Inline LeftArrow SVG — the design-system sprite icon hardcodes
 * `stroke="#25252B"` and is rendered through `<use>` from a hidden symbol,
 * so neither `color` nor a descendant `& path { stroke }` selector can
 * recolor it in this context. Inlining lets us drive the stroke directly. */
const ArrowIcon = ({direction = 'left', color = 'currentColor', size = '24px'}) => (
    <Box
        as="svg"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
        {...styles.arrowIcon({direction, size})}
    >
        <path
            d="M2 11.9999H22M2 11.9999L9.955 19.9549M2 11.9999L9.955 4.04492"
            {...styles.arrowIconPath(color)}
        />
    </Box>
)
ArrowIcon.propTypes = {
    direction: PropTypes.oneOf(['left', 'right']),
    color: PropTypes.string,
    size: PropTypes.string
}

const GDIHeroSliderBlock = ({
    HideBlockOn,
    SliderItems,
    TooltipContent,
    GDIHeroSliderBlockAutoplay,
    GDIHeroSliderBlockTimeBetweenSlides,
    GDIHeroSliderBlockInfiniteMode,
    GDIHeroSliderBlockHidePlayPauseIcon,
    GDIHeroSliderBlockAudienceTargeting
}) => {
    const audienceMatches = useIsAudienceTargetMatch(GDIHeroSliderBlockAudienceTargeting)
    const hideStyles = getGDIHideStyles(HideBlockOn)
    /* Header variants 1 & 2 are transparent overlays — they sit on top of the
     * hero, so the play/pause icon needs to clear the header. Variant 3 is a
     * solid header that pushes the hero down on its own; keep the spec offset. */
    const {headerVariant, headerHeight} = useHeaderContext()
    const isHeaderOverlay = headerVariant === '1' || headerVariant === '2'
    const floatingControlsTop = isHeaderOverlay
        ? `${(headerHeight || 0) + 16}px`
        : {base: '16px', lg: '24px'}
    const isMobileTooltip = useBreakpointValue({base: true, lg: false})
    const swiperRef = useRef(null)
    /* Progress line ref — width is updated directly via Swiper's
     * onAutoplayTimeLeft to avoid the re-render cost of state updates at the
     * 60fps cadence the callback fires at. */
    const progressLineRef = useRef(null)
    const mobileBarsFillRefs = useRef([])
    const touchStartRef = useRef(null)
    const didTouchMoveRef = useRef(false)
    /* Refs to overlay slide DOM nodes + teaser text nodes. Their opacity
     * is updated imperatively from Swiper's onProgress / onSetTranslate
     * callbacks so the fade tracks each slide's on-screen position
     * frame-by-frame (drag, autoplay tween, programmatic nav). */
    const overlaySlideRefs = useRef([])
    const teaserEyebrowRef = useRef(null)
    const teaserTitleRef = useRef(null)
    /* True while Swiper is running its own slide-change transition
     * (arrow click, autoplay step, drag-release snap). During this
     * window Swiper drives the slide via a CSS transform transition
     * and does NOT fire onProgress/onSetTranslate per frame, so we
     * fall back to a CSS opacity transition instead of per-frame
     * imperative updates. */
    const isInTransitionRef = useRef(false)
    /* Pending text-swap timer for the midpoint of a programmatic
     * transition. Tracked so consecutive transitions can cancel a
     * stale swap before it fires. */
    const transitionSwapTimeoutRef = useRef(null)
    /* Mirror of `displayedTextIndex` available synchronously inside
     * Swiper callbacks (which capture stale state via closure). */
    const displayedTextIndexRef = useRef(0)
    const [activeIndex, setActiveIndex] = useState(0)
    /* Index whose text is currently rendered in the overlay (and the
     * desktop teaser). Updated on slideChange so when Swiper commits to a
     * new slide we immediately swap the displayed text — the new slide's
     * progress is still non-zero at that moment, so opacity is low and
     * fades up to 1 as the slide settles into place. */
    const [displayedTextIndex, setDisplayedTextIndex] = useState(0)
    /* Keep ref in sync so Swiper callbacks always read the latest value. */
    displayedTextIndexRef.current = displayedTextIndex
    const [isAutoplayRunning, setIsAutoplayRunning] = useState(!!GDIHeroSliderBlockAutoplay)

    const setProgressWidth = (pct) => {
        const node = progressLineRef.current
        if (node) node.style.width = `${pct}%`
    }

    const setMobileBarWidth = (barIdx, pct) => {
        const node = mobileBarsFillRefs.current[barIdx]
        if (!node) return
        const clampedPct = Math.max(0, Math.min(100, pct))
        node.style.width = `${clampedPct}%`
    }

    /* Build a `realIndex → closest progress` map from swiper.slides. In
     * loop mode swiper duplicates slides, so we keep the entry with the
     * smallest |progress| per real index. `slide.progress` is provided by
     * `watchSlidesProgress` and updates continuously during drag and
     * during the post-release / autoplay tween. */
    const collectSlideProgress = (swiper) => {
        const map = {}
        const slidesEls = swiper.slides || []
        for (let i = 0; i < slidesEls.length; i += 1) {
            const el = slidesEls[i]
            const attr = el.getAttribute && el.getAttribute('data-swiper-slide-index')
            const realIdx = attr !== null && attr !== undefined && attr !== '' ? Number(attr) : i
            const p = typeof el.progress === 'number' ? el.progress : 0
            if (!(realIdx in map) || Math.abs(p) < Math.abs(map[realIdx])) {
                map[realIdx] = p
            }
        }
        return map
    }

    /* Update overlay slide + teaser opacities from the latest swiper
     * progress. Opacity follows slider position directly:
     *   opacity = 1 - |progress|
     * → 1 when the slide is centered, 0.5 at the midpoint between
     * slides, never below that on the way to the next slide. The
     * content of the closest slide is shown; we swap it at the
     * midpoint (|progress| === 0.5), so the user sees a smooth
     * 1 → 0.5 fade of the old text, an instant content swap, then a
     * 0.5 → 1 fade-in of the new text — with no fully blank moment. */
    const applySlideProgressOpacities = (swiper) => {
        /* Skip per-frame updates while Swiper runs its own transition
         * (arrow click, drag-release snap, autoplay step). During that
         * window opacity is animated via CSS transitions set by
         * `onSlideChangeTransitionStart`. */
        if (isInTransitionRef.current) return
        const map = collectSlideProgress(swiper)
        /* Find the slide closest to centered (smallest |progress|). That
         * is the one whose text we show; everything else stays at
         * opacity 0 so there's never a frame with two stacked overlays. */
        let closestIdx = swiper.realIndex
        let closestAbs = Infinity
        Object.entries(map).forEach(([idxStr, p]) => {
            const a = Math.abs(p)
            if (a < closestAbs) {
                closestAbs = a
                closestIdx = Number(idxStr)
            }
        })
        const closestP = closestIdx in map ? map[closestIdx] : 0
        const value = Math.max(0, 1 - Math.abs(closestP))
        overlaySlideRefs.current.forEach((node, idx) => {
            if (!node) return
            node.style.opacity = idx === closestIdx ? String(value) : '0'
        })
        const teaserOpacity = String(value)
        if (teaserEyebrowRef.current) {
            teaserEyebrowRef.current.style.opacity = teaserOpacity
        }
        if (teaserTitleRef.current) {
            teaserTitleRef.current.style.opacity = teaserOpacity
        }
        /* Swap the text content at the midpoint. State updates are
         * cheap-but-not-free, so guard with a comparison so we only
         * re-render when the closest slide actually changes. */
        if (closestIdx !== displayedTextIndex) {
            setDisplayedTextIndex(closestIdx)
        }
    }

    /* Helpers for the CSS-transition fade used during programmatic /
     * drag-release slide changes. Sets a transition string on every
     * overlay slide + the teaser refs, then clears it after the
     * transition completes so subsequent drags get frame-accurate
     * imperative updates again. */
    const setOverlayTransition = (durationMs) => {
        const trans = durationMs > 0 ? `opacity ${durationMs}ms ease` : ''
        overlaySlideRefs.current.forEach((n) => {
            if (n) n.style.transition = trans
        })
        if (teaserEyebrowRef.current) teaserEyebrowRef.current.style.transition = trans
        if (teaserTitleRef.current) teaserTitleRef.current.style.transition = trans
    }

    /* Set opacities to 0 on every overlay except `visibleIdx`, which
     * gets `value`. The teaser refs follow the same value (they
     * always render the displayed slide's text). */
    const setOverlayTargets = (visibleIdx, value) => {
        overlaySlideRefs.current.forEach((n, i) => {
            if (!n) return
            n.style.opacity = i === visibleIdx ? String(value) : '0'
        })
        const v = String(value)
        if (teaserEyebrowRef.current) teaserEyebrowRef.current.style.opacity = v
        if (teaserTitleRef.current) teaserTitleRef.current.style.opacity = v
    }

    /* Cleanup on unmount — cancel any pending midpoint swap timer so
     * we don't update state on an unmounted component. */
    useEffect(
        () => () => {
            if (transitionSwapTimeoutRef.current) {
                clearTimeout(transitionSwapTimeoutRef.current)
                transitionSwapTimeoutRef.current = null
            }
        },
        []
    )

    if (hideStyles.doNotLoadComponent) return null
    if (!audienceMatches) return null

    /* SliderItems is an inline list — each item carries SlideContent,
     * ArrowsTeaserEyebrow and ArrowsTeaserTitle as content areas
     * (ContentAreaItemModel arrays). Eyebrow/title are authored as
     * GDITextBlocks so the CMS controls their copy and styling. */
    const sliderItems = Array.isArray(SliderItems) ? SliderItems : []
    const slides = sliderItems
        .map((item, idx) => {
            const blocks = getAllExpandedFromContentArea(item?.SlideContent)
            const eyebrowBlock =
                getAllExpandedFromContentArea(item?.ArrowsTeaserEyebrow)[0] || null
            const titleBlock =
                getAllExpandedFromContentArea(item?.ArrowsTeaserTitle)[0] || null
            return {
                key: blocks[0]?.ContentLink?.GuidValue || `slide-${idx}`,
                blocks,
                eyebrow: eyebrowBlock,
                title: titleBlock
            }
        })
        .filter((s) => s.blocks.length > 0)
    const hasTooltip = getAllExpandedFromContentArea(TooltipContent).length > 0

    if (slides.length === 0) return null

    const delay = Number.parseInt(GDIHeroSliderBlockTimeBetweenSlides, 10) || 7000
    const hasMultiple = slides.length > 1
    const displayedTextSlide = slides[displayedTextIndex] || slides[0]
    const showPlayPause =
        hasMultiple && !GDIHeroSliderBlockHidePlayPauseIcon && GDIHeroSliderBlockAutoplay
    const initialMobileFillPct = GDIHeroSliderBlockAutoplay ? 0 : 100

    const goPrev = () => swiperRef.current?.swiper?.slidePrev()
    const goNext = () => swiperRef.current?.swiper?.slideNext()
    const goToSlide = (idx) => {
        const swiper = swiperRef.current?.swiper
        if (!swiper) return
        if (GDIHeroSliderBlockInfiniteMode) {
            swiper.slideToLoop(idx)
            return
        }
        swiper.slideTo(idx)
    }
    const syncMobileBars = (currentIdx, currentPct) => {
        slides.forEach((_, idx) => {
            setMobileBarWidth(idx, idx === currentIdx ? currentPct : 0)
        })
    }

    const handleMobileTouchStart = (event) => {
        if (!hasMultiple || !isMobileTooltip) return
        const touch = event.touches?.[0]
        if (!touch) return
        touchStartRef.current = {x: touch.clientX, y: touch.clientY}
        didTouchMoveRef.current = false
    }

    const handleMobileTouchMove = (event) => {
        if (!hasMultiple || !isMobileTooltip) return
        const touch = event.touches?.[0]
        if (!touch || !touchStartRef.current) return
        const dx = Math.abs(touch.clientX - touchStartRef.current.x)
        const dy = Math.abs(touch.clientY - touchStartRef.current.y)
        if (dx > 10 || dy > 10) {
            didTouchMoveRef.current = true
        }
    }

    const handleMobileTapNavigate = (event) => {
        if (!hasMultiple || !isMobileTooltip) return
        if (didTouchMoveRef.current) {
            didTouchMoveRef.current = false
            return
        }
        if (event.target.closest('[data-skip-mobile-tap-nav="true"]')) return

        const swiperEl = swiperRef.current?.el || swiperRef.current?.swiper?.el
        if (!swiperEl) return
        const rect = swiperEl.getBoundingClientRect()
        const x = event.clientX
        if (x < rect.left || x > rect.right) return

        const midpoint = rect.left + rect.width / 2
        if (x < midpoint) {
            goPrev()
        } else {
            goNext()
        }
    }

    const togglePlay = () => {
        const autoplay = swiperRef.current?.swiper?.autoplay
        if (!autoplay) return
        if (isAutoplayRunning) {
            autoplay.stop()
            setIsAutoplayRunning(false)
        } else {
            autoplay.start()
            setIsAutoplayRunning(true)
        }
    }

    return (
        <Box
            data-block-type="GDIHeroSliderBlock"
            sx={hideStyles.sx}
            onTouchStartCapture={handleMobileTouchStart}
            onTouchMoveCapture={handleMobileTouchMove}
            onClickCapture={handleMobileTapNavigate}
            {...styles.root}
        >
            <Swiper
                ref={swiperRef}
                modules={[Autoplay]}
                slidesPerView={1}
                speed={styles.SLIDE_TRANSITION_SPEED}
                watchSlidesProgress
                loop={!!GDIHeroSliderBlockInfiniteMode}
                autoplay={GDIHeroSliderBlockAutoplay ? {delay, disableOnInteraction: false} : false}
                onSlideChangeTransitionStart={(swiper) => {
                    /* Programmatic / drag-release transition starting.
                     * Swiper drives the slide via a CSS transform
                     * transition over `speed` ms and does NOT fire
                     * onProgress per frame, so animate opacity via CSS:
                     * fade out current text in the first half, swap
                     * text + fade in the new one in the second half. */
                    if (!hasMultiple) return
                    isInTransitionRef.current = true
                    const speed = styles.SLIDE_TRANSITION_SPEED
                    const half = Math.max(1, Math.round(speed / 2))
                    const fromIdx = displayedTextIndexRef.current
                    const toIdx = swiper.realIndex

                    if (transitionSwapTimeoutRef.current) {
                        clearTimeout(transitionSwapTimeoutRef.current)
                        transitionSwapTimeoutRef.current = null
                    }

                    /* Phase 1 — fade currently displayed text from its
                     * current opacity down to 0.5 over `half` ms,
                     * matching the |progress| 0 → 0.5 ramp the drag
                     * path uses. Never reaches 0, so there's no fully
                     * blank moment. */
                    setOverlayTransition(half)
                    setOverlayTargets(fromIdx, 0.5)

                    /* Phase 2 — at the midpoint, swap the displayed
                     * text and animate the new overlay 0.5 → 1 over
                     * the remaining `half` ms (mirrors |progress| 0.5
                     * → 0). We update React state to swap the rendered
                     * text content; the next frame (rAF) re-applies
                     * transition + opacity so React has committed
                     * before the fade-in starts. To avoid a flash, we
                     * pre-set the new overlay's opacity to 0.5 with no
                     * transition before swapping content. */
                    transitionSwapTimeoutRef.current = setTimeout(() => {
                        transitionSwapTimeoutRef.current = null
                        /* Pre-position the incoming overlay at 0.5
                         * with no transition so the React content swap
                         * doesn't show a snap from 0 → 0.5. */
                        setOverlayTransition(0)
                        setOverlayTargets(toIdx, 0.5)
                        setDisplayedTextIndex(toIdx)
                        displayedTextIndexRef.current = toIdx
                        requestAnimationFrame(() => {
                            setOverlayTransition(half)
                            setOverlayTargets(toIdx, 1)
                        })
                    }, half)
                }}
                onSlideChangeTransitionEnd={() => {
                    /* Transition finished \u2014 clear the CSS transition so
                     * the next user drag updates opacity per frame
                     * without easing lag. */
                    isInTransitionRef.current = false
                    setOverlayTransition(0)
                }}
                onTouchStart={() => {
                    /* User started a new touch. Cancel any in-flight
                     * programmatic transition swap so per-frame
                     * imperative updates take over again. */
                    if (transitionSwapTimeoutRef.current) {
                        clearTimeout(transitionSwapTimeoutRef.current)
                        transitionSwapTimeoutRef.current = null
                    }
                    isInTransitionRef.current = false
                    setOverlayTransition(0)
                }}
                onSlideChange={(swiper) => {
                    setActiveIndex(swiper.realIndex)
                    /* Note: `displayedTextIndex` is intentionally NOT
                     * updated here — it's swapped at the midpoint by
                     * either `applySlideProgressOpacities` (drag) or
                     * the Phase 2 setTimeout in
                     * `onSlideChangeTransitionStart` (arrow / autoplay).
                     * Updating it here would race the fade-out and swap
                     * the text instantly. */
                    /* Reset the per-slide progress fill — it will start
                     * filling again on the next onAutoplayTimeLeft tick. */
                    setProgressWidth(0)
                    syncMobileBars(swiper.realIndex, GDIHeroSliderBlockAutoplay ? 0 : 100)
                }}
                onAfterInit={(swiper) => {
                    setIsAutoplayRunning(!!swiper?.autoplay?.running)
                    syncMobileBars(swiper.realIndex, initialMobileFillPct)
                    applySlideProgressOpacities(swiper)
                }}
                /* Fires whenever the swiper progress changes — user drag,
                 * autoplay tween, programmatic slideTo. Drives the fade
                 * frame-by-frame so the text opacity tracks the actual
                 * on-screen position of each slide. */
                onProgress={(swiper) => {
                    applySlideProgressOpacities(swiper)
                }}
                onSetTranslate={(swiper) => {
                    /* Some swiper builds update `slide.progress` slightly
                     * before / after the global progress event during fast
                     * drags; hooking both keeps the fade glued to the
                     * touch position. */
                    applySlideProgressOpacities(swiper)
                }}
                /* Fires continuously while autoplay is running. `progress`
                 * goes 1 → 0 over the slide's display time, so we invert it
                 * for an empty → full fill effect. */
                onAutoplayTimeLeft={(swiper, _time, progress) => {
                    const fillPct = (1 - progress) * 100
                    setProgressWidth(fillPct)
                    setMobileBarWidth(swiper.realIndex, fillPct)
                }}
            >
                {slides.map((slide, idx) => (
                    <SwiperSlide key={slide.key || idx}>
                        <Box {...styles.swiperHideContent}>
                            {slide.blocks.map((block, bIdx) => (
                                <React.Fragment
                                    key={block?.ContentLink?.GuidValue || `${idx}-${bIdx}`}
                                >
                                    {renderComponentsSwitch(block)}
                                </React.Fragment>
                            ))}
                        </Box>
                    </SwiperSlide>
                ))}
            </Swiper>

            {/* Overlay layer for non-image content. Each slide is rendered
             * with media hidden (visibility: hidden preserves layout so the
             * authored content position matches the Swiper layer exactly).
             * Opacity is driven imperatively from Swiper's onProgress so
             * the fade follows each slide's actual on-screen position
             * (drag, autoplay, programmatic nav). */}
            <Box data-skip-mobile-tap-nav="true" {...styles.contentOverlay}>
                {slides.map((slide, idx) => {
                    const isActive = idx === displayedTextIndex
                    return (
                        <Box
                            ref={(el) => {
                                overlaySlideRefs.current[idx] = el
                            }}
                            key={`overlay-${slide.key || idx}`}
                            aria-hidden={!isActive}
                            {...styles.contentOverlaySlide({isActive})}
                        >
                            {slide.blocks.map((block, bIdx) => (
                                <React.Fragment
                                    key={block?.ContentLink?.GuidValue || `${idx}-${bIdx}`}
                                >
                                    {renderComponentsSwitch(block)}
                                </React.Fragment>
                            ))}
                        </Box>
                    )
                })}
            </Box>

            <Flex data-skip-mobile-tap-nav="true" {...styles.mobileNav}>
                {slides.map((slide, idx) => (
                    <Box
                        as="button"
                        type="button"
                        aria-label={`Go to slide ${idx + 1}`}
                        onClick={() => goToSlide(idx)}
                        key={`mobile-nav-${slide.key || idx}`}
                        {...styles.mobileNavButton}
                    >
                        <Box
                            ref={(el) => {
                                mobileBarsFillRefs.current[idx] = el
                            }}
                            {...styles.mobileNavFill({
                                isActive: idx === activeIndex,
                                initialMobileFillPct
                            })}
                        />
                    </Box>
                ))}
            </Flex>

            {hasTooltip && (
                <Box
                    data-skip-mobile-tap-nav="true"
                    {...styles.tooltipControl(floatingControlsTop)}
                >
                    <GDITooltip tooltipContent={TooltipContent} placement="right" />
                </Box>
            )}

            {/* Top-right play/pause toggle. Only rendered when autoplay is on
             * and the author hasn't hidden it. Position mirrors the UK
             * heroPanelBlock `controls` slot. */}
            {showPlayPause && (
                <IconButton
                    aria-label={isAutoplayRunning ? 'Pause slideshow' : 'Play slideshow'}
                    onClick={togglePlay}
                    icon={isAutoplayRunning ? <Pause /> : <Play />}
                    data-skip-mobile-tap-nav="true"
                    {...styles.playPauseButton(floatingControlsTop)}
                />
            )}

            {/* Bottom-right teaser block: per-slide eyebrow + title with
             * prev/next arrow controls and a per-slide autoplay progress
             * line. Layout mirrors the UK heroSliderCustomNavigation:
             * eyebrow on top, title + arrows in a flex row, progress line
             * spanning the teaser block width. */}
            {hasMultiple && (
                <Box {...styles.desktopTeaser}>
                    {displayedTextSlide.eyebrow && (
                        <Box ref={teaserEyebrowRef} {...styles.teaserEyebrow}>
                            {renderComponentsSwitch(displayedTextSlide.eyebrow)}
                        </Box>
                    )}
                    <Flex {...styles.teaserRow}>
                        <Box ref={teaserTitleRef} {...styles.teaserTitle}>
                            {displayedTextSlide.title && renderComponentsSwitch(displayedTextSlide.title)}
                        </Box>
                        <Flex {...styles.desktopControls}>
                            <IconButton
                                aria-label="Previous slide"
                                onClick={goPrev}
                                icon={<ArrowIcon direction="left" />}
                                {...styles.arrowButton}
                            />
                            <IconButton
                                aria-label="Next slide"
                                onClick={goNext}
                                icon={<ArrowIcon direction="right" />}
                                {...styles.arrowButton}
                            />
                        </Flex>
                    </Flex>
                    {/* Autoplay progress line — fills 0 → 100% across each
                     * slide's display interval. Hidden when autoplay is off
                     * (no time-until-next signal exists). */}
                    {GDIHeroSliderBlockAutoplay && (
                        <Box {...styles.desktopProgressTrack}>
                            <Box ref={progressLineRef} {...styles.desktopProgressFill} />
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    )
}

GDIHeroSliderBlock.propTypes = {
    HideBlockOn: PropTypes.string,
    SliderItems: PropTypes.arrayOf(
        PropTypes.shape({
            SlideContent: PropTypes.array,
            ArrowsTeaserEyebrow: PropTypes.array,
            ArrowsTeaserTitle: PropTypes.array
        })
    ),
    TooltipContent: PropTypes.array,
    GDIHeroSliderBlockAutoplay: PropTypes.bool,
    GDIHeroSliderBlockTimeBetweenSlides: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    GDIHeroSliderBlockInfiniteMode: PropTypes.bool,
    GDIHeroSliderBlockHidePlayPauseIcon: PropTypes.bool,
    GDIHeroSliderBlockAudienceTargeting: PropTypes.object
}

export default GDIHeroSliderBlock
